// src/components/Admin/Graphs.jsx
import React from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Card,
    CardContent,
    useTheme, // Import useTheme for colors
} from '@mui/material';

// Import Recharts components
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';

// Custom hook or function to process user data for organization chart
const processUsersForOrgChart = (users) => {
  if (!users || users.length === 0) return [];
  const orgCounts = users.reduce((acc, user) => {
    const org = user.organization || 'Unknown Organization';
    acc[org] = (acc[org] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(orgCounts).map(([name, count]) => ({ name, count }));
};

// Custom function to process coinbox data for total value chart
const processCoinboxForTotalValueChart = (coinbox) => {
    if (!coinbox || coinbox.length === 0) return [];
    const totalRental = coinbox.reduce((sum, locker) => sum + (locker.rentalcoinvalue || 0), 0);
    const totalExtend = coinbox.reduce((sum, locker) => sum + (locker.extendvalue || 0), 0);
    const totalPenalty = coinbox.reduce((sum, locker) => sum + (locker.penaltyvalue || 0), 0);

    return [
        { name: 'Total Rental Value', value: totalRental },
        { name: 'Total Extend Value', value: totalExtend },
        { name: 'Total Penalty Value', value: totalPenalty },
    ].filter(item => item.value > 0); // Only include if value > 0
};

const Graphs = ({ users, uLoading, uError, coinbox, cLoading, cError, displayMode }) => {
    const theme = useTheme(); // Get theme for colors

    // Process data when props change
    const orgChartData = React.useMemo(() => processUsersForOrgChart(users), [users]);
    // coinbox array is already suitable for per-locker value and count charts
    const lockerData = React.useMemo(() => coinbox, [coinbox]);
    const totalCoinboxValueChartData = React.useMemo(() => processCoinboxForTotalValueChart(coinbox), [coinbox]);

    // Pie chart colors (can reuse or define separate for counts if needed)
    const PIE_COLORS = [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        // Add more colors if you have more categories
    ];

    // ─── Render User Statistics Graphs ───────────────────────────────────────
    const renderUserStats = () => {
        if (uLoading) {
            return (
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Grid>
            );
        }
        if (uError) {
            return (
                <Grid item xs={12}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {uError}
                    </Alert>
                </Grid>
            );
        }
        if (users.length === 0) {
            return (
                <Grid item xs={12}>
                    <Typography variant="body1" align="center" sx={{ mt: 2 }}>No user data found to display graphs.</Typography>
                </Grid>
            );
        }

        return (
            <>
                {/* Total Users Card */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Total Users</Typography>
                            <Typography variant="h3" color="primary.main">{users.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Users by Organization Chart */}
                <Grid item xs={12} md={8}> {/* Takes remaining space in the row on medium+ screens */}
                    <Paper sx={{ p: 2 }}> {/* Use Paper for elevation */}
                        <Typography variant="h6" gutterBottom align="center">Users by Department</Typography>
                        {orgChartData.length > 0 ? (
                            <Box sx={{ width: '100%', height: 300 }}> {/* Set a fixed height for the chart container */}
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={orgChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5, }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" angle={-15} textAnchor="end" height={50} style={{ fontSize: '12px' }} /> {/* Added font size */}
                                        <YAxis label={{ value: 'Number of Users', angle: -90, position: 'insideLeft' }} />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill={theme.palette.secondary.main} name="Number of Users" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                            <Typography variant="body1" align="center" sx={{ mt: 2 }}>No organization data available for charting.</Typography>
                        )}
                    </Paper>
                </Grid>
            </>
        );
    };

    // ─── Render Coinbox Statistics Graphs ────────────────────────────────────
    const renderCoinboxStats = () => {
        if (cLoading) {
            return (
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Grid>
            );
        }
        if (cError) {
            return (
                <Grid item xs={12}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {cError}
                    </Alert>
                </Grid>
            );
        }
        if (coinbox.length === 0) {
            return (
                <Grid item xs={12}>
                    <Typography variant="body1" align="center" sx={{ mt: 2 }}>No coinbox data found to display graphs.</Typography>
                </Grid>
            );
        }

        // Assuming we want to show per-locker values and counts side-by-side on large screens,
        // and the total value distribution below.

        return (
            <>
                {/* Total Lockers Card */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Total Lockers</Typography>
                            <Typography variant="h3" color="primary.main">{coinbox.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                 {/* Placeholder / Another Metric in the first row */}
                 {/* This grid item balances the row layout with the Total Lockers card */}
                 {/* You could put another card or small chart here if needed */}
                  <Grid item xs={12} sm={6} md={8}>
                      <Card>
                          <CardContent>
                              <Typography variant="h6" gutterBottom>Coinbox Overview</Typography>
                               <Typography variant="body1">Select a chart below to view details.</Typography>
                          </CardContent>
                      </Card>
                  </Grid>


                {/* Coinbox Values per Locker (Bar Chart) */}
                <Grid item xs={12} md={6}> {/* Half width on medium+ screens */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom align="center">Coinbox Values per Locker</Typography>
                        {lockerData.length > 0 ? (
                            <Box sx={{ width: '100%', height: 350 }}> {/* Set a fixed height */}
                                <ResponsiveContainer width="100%" height="100%">
                                     {/* Use a unique ID for the chart if you have multiple charts in the same view */}
                                     <BarChart data={lockerData} margin={{ top: 5, right: 30, left: 20, bottom: 5, }}>
                                         <CartesianGrid strokeDasharray="3 3" />
                                         <XAxis dataKey="id" angle={-15} textAnchor="end" height={50} style={{ fontSize: '12px' }} />
                                         <YAxis label={{ value: 'Value', angle: -90, position: 'insideLeft' }} />
                                         <Tooltip />
                                         <Legend />
                                         <Bar dataKey="rentalcoinvalue" fill={theme.palette.info.main} name="Rental Value" />
                                         <Bar dataKey="extendvalue" fill={theme.palette.success.main} name="Extend Value" />
                                         <Bar dataKey="penaltyvalue" fill={theme.palette.error.main} name="Penalty Value" />
                                     </BarChart>
                                 </ResponsiveContainer>
                            </Box>
                        ) : (
                             <Typography variant="body1" align="center" sx={{ mt: 2 }}>No per-locker value data available for charting.</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Coinbox Counts per Locker (New Bar Chart) */}
                 <Grid item xs={12} md={6}> {/* Half width on medium+ screens */}
                     <Paper sx={{ p: 2 }}>
                         <Typography variant="h6" gutterBottom align="center">Coinbox Counts per Locker</Typography>
                         {lockerData.length > 0 ? (
                             <Box sx={{ width: '100%', height: 350 }}> {/* Set a fixed height */}
                                 <ResponsiveContainer width="100%" height="100%">
                                     <BarChart data={lockerData} margin={{ top: 5, right: 30, left: 20, bottom: 5, }}>
                                         <CartesianGrid strokeDasharray="3 3" />
                                         <XAxis dataKey="id" angle={-15} textAnchor="end" height={50} style={{ fontSize: '12px' }} />
                                         <YAxis label={{ value: 'Number of Coins', angle: -90, position: 'insideLeft' }} />
                                         <Tooltip />
                                         <Legend />
                                         {/* Use different colors or shades from the value chart */}
                                         <Bar dataKey="rentalcoincount" fill={theme.palette.primary.main} name="Rental Coins" />
                                         <Bar dataKey="extendcoin" fill={theme.palette.warning.main} name="Extend Coins" />
                                         <Bar dataKey="penaltycoin" fill={theme.palette.error.dark} name="Penalty Coins" />
                                     </BarChart>
                                 </ResponsiveContainer>
                             </Box>
                         ) : (
                              <Typography variant="body1" align="center" sx={{ mt: 2 }}>No per-locker count data available for charting.</Typography>
                         )}
                     </Paper>
                 </Grid>


                {/* Total Coinbox Value Distribution (Pie Chart) */}
                <Grid item xs={12} md={6}> {/* Half width on medium+ screens */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom align="center">Total Coinbox Value Distribution</Typography>
                        {totalCoinboxValueChartData.length > 0 ? (
                            <Box sx={{ width: '100%', height: 300 }}> {/* Set a fixed height */}
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={totalCoinboxValueChartData}
                                            cx="50%" // Center X
                                            cy="50%" // Center Y
                                            outerRadius={100} // Size of the pie
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} // Show name and percentage
                                            labelLine={false} // Hide lines to labels
                                        >
                                            {/* Map data to cells for individual colors */}
                                            {totalCoinboxValueChartData.map((entry, index) => (
                                                <Cell key={`cell-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} /> {/* Added formatter for currency */}
                                        <Legend /> {/* Add legend */}
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                             <Typography variant="body1" align="center" sx={{ mt: 2 }}>No total value data available for charting.</Typography>
                        )}
                    </Paper>
                </Grid>

                 {/* Another Metric or Summary Placeholder (moved to match grid) */}
                  <Grid item xs={12} md={6}>
                      <Card>
                          <CardContent>
                              <Typography variant="h6" gutterBottom>Coinbox Totals (Coins)</Typography>
                              {/* Optionally add total coin count here or a separate chart */}
                              <Typography variant="body1">Total Rental Coins: {lockerData.reduce((sum, l) => sum + (l.rentalcoincount || 0), 0)}</Typography>
                               <Typography variant="body1">Total Extend Coins: {lockerData.reduce((sum, l) => sum + (l.extendcoin || 0), 0)}</Typography>
                               <Typography variant="body1">Total Penalty Coins: {lockerData.reduce((sum, l) => sum + (l.penaltycoin || 0), 0)}</Typography>

                         </CardContent>
                     </Card>
                 </Grid>


            </>
        );
    };

    // ─── Render based on displayMode ─────────────────────────────────────────
    return (
        <>
            {displayMode === "userStats" && renderUserStats()}
            {displayMode === "coinboxStats" && renderCoinboxStats()}
        </>
    );
};

export default Graphs;